import type { ReactNode } from "react"

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom"

import { SolarContractDrawer } from "./contract/SolarContractDrawer"
import { SolarDesignerRoute } from "./designer/SolarDesignerRoute"
import { SolarShareDrawer } from "./share/SolarShareDrawer"
import {
  solarProposalPath,
  solarSetupGroupFromPath,
  solarSetupMenuPath,
  solarSetupStepFromPath,
} from "./solar-setup-paths"
import { SolarFinancingStatusRoute } from "./SolarFinancingStatusStep"
import { SolarProposalShell } from "./SolarProposalShell"
import { SolarSummaryGate } from "./summary/SolarSummaryGate"

/**
 * A group the rail doesn't list falls back to the rail itself. It reads the
 * raw path, as the rail does, so an encoded id the router would decode into a
 * known group (`pric%69ng`) can't leave a setup URL with the rail closed.
 */
const SolarSetupGroupRoute = (): JSX.Element | null => {
  const { proposalId = `` } = useParams<{ proposalId: string }>()
  const { hash, pathname, search } = useLocation()
  return solarSetupGroupFromPath(pathname, proposalId) != null ? null : (
    <Navigate to={`${solarSetupMenuPath(proposalId)}${search}${hash}`} replace />
  )
}

/** Likewise a step the raw path doesn't name falls back to the proposal, so
 * the URL never reads as a step with no drawer open. A step the route renders
 * itself (Contract status) passes its drawer as children. */
const SolarSetupStepRoute = ({
  children = null,
}: {
  children?: ReactNode
}): JSX.Element | null => {
  const { proposalId = `` } = useParams<{ proposalId: string }>()
  const { hash, pathname, search } = useLocation()
  return solarSetupStepFromPath(pathname, proposalId) != null ? (
    <>{children}</>
  ) : (
    <Navigate to={`${solarProposalPath(proposalId)}${search}${hash}`} replace />
  )
}

export const SolarProposalRoutes = (): JSX.Element => (
  <Routes>
    <Route path="solar-proposal/:proposalId" element={<SolarProposalShell />}>
      {/* The shell renders the rail and the step drawer from the path and owns
          the read-only gate, which needs the proposal read; a read-only actor
          or `sample` is sent back to the proposal before these render, except
          that a read-only actor keeps Contract and Financing Status. */}
      {/* The shell renders the Summary from the path, in front of everything
          else while an editor hasn't confirmed it. */}
      <Route path="summary/*" element={null} />
      <Route path="setup" element={null} />
      <Route path="setup/:groupId" element={<SolarSetupGroupRoute />} />
      <Route path="site" element={<SolarSetupStepRoute />} />
      <Route path="equipment" element={<SolarSetupStepRoute />} />
      <Route path="compute" element={<SolarSetupStepRoute />} />
      <Route path="loan-product" element={<SolarSetupStepRoute />} />
      {/* Financing Status renders from the route too, as Contract does. */}
      <Route
        path="financing-status"
        element={
          <SolarSetupStepRoute>
            <SolarFinancingStatusRoute />
          </SolarSetupStepRoute>
        }
      />
      <Route path="utility" element={<SolarSetupStepRoute />} />
      <Route path="consumption" element={<SolarSetupStepRoute />} />
      <Route path="greenbutton" element={<SolarSetupStepRoute />} />
      <Route path="system-pricing" element={<SolarSetupStepRoute />} />
      <Route path="design" element={<SolarSetupStepRoute />} />
      <Route path="adders" element={<SolarSetupStepRoute />} />
      <Route path="warranty" element={<SolarSetupStepRoute />} />
      <Route path="incentives" element={<SolarSetupStepRoute />} />
      <Route path="vpp-programs" element={<SolarSetupStepRoute />} />
      <Route path="hdm" element={<SolarSetupStepRoute />} />
      <Route path="share" element={<SolarShareDrawer />} />
      {/* The shared Contract drawer renders from the route rather than the
          setup drawer, so a read-only viewer can open it too. */}
      <Route
        path="contract"
        element={
          <SolarSetupStepRoute>
            <SolarContractDrawer />
          </SolarSetupStepRoute>
        }
      />
    </Route>
    {/* The designer is 100vw/100vh and brings its own chrome, so it sits
        beside the shell rather than inside its layout. */}
    <Route
      path="solar-proposal/:proposalId/designer"
      element={
        <SolarSummaryGate>
          <SolarDesignerRoute />
        </SolarSummaryGate>
      }
    />
  </Routes>
)
